import { useCallback, useContext, useEffect, useState } from "react";

import { AuthContext } from "../context/AuthContext";
import {
  ComplianceCard,
  getDocumentComplianceApi,
} from "../services/Compliance/ComplianceApis";
import { getCompanyMembersApi } from "../services/Member/MemberApis";
import {
  COMPLIANCE_CARD_META,
  ComplianceCardKey,
  isDeductionBelowPremium,
} from "../utils/documentCompliance";
import { isValidSACellphone } from "../utils/systemValidation";

const POLL_MS = 45000;

export type ComplianceIssueCardSummary = {
  key: ComplianceCardKey;
  label: string;
  description: string;
  count: number;
};

const toSummary = (card: {
  Key: string;
  Label?: string;
  Description?: string;
  MissingCount?: number;
}): ComplianceIssueCardSummary | null => {
  const key = card.Key as ComplianceCardKey;
  const meta = COMPLIANCE_CARD_META[key];
  const count = Number(card.MissingCount) || 0;
  if (count <= 0) return null;
  return {
    key,
    label: card.Label || meta?.Label || key,
    description: card.Description || meta?.Description || "",
    count,
  };
};

const buildInvalidCellphoneSummary = (
  members: any[]
): ComplianceIssueCardSummary | null => {
  const count = (Array.isArray(members) ? members : []).filter(
    (member) => !isValidSACellphone(member?.Cellphone)
  ).length;
  if (count <= 0) return null;
  const meta = COMPLIANCE_CARD_META.InvalidCellphone;
  return {
    key: "InvalidCellphone",
    label: meta.Label,
    description: meta.Description,
    count,
  };
};

const buildDeductionBelowPremiumSummary = (
  members: any[]
): ComplianceIssueCardSummary | null => {
  const count = (Array.isArray(members) ? members : []).filter((member) =>
    isDeductionBelowPremium(member)
  ).length;
  if (count <= 0) return null;
  const meta = COMPLIANCE_CARD_META.DeductionBelowPremium;
  return {
    key: "DeductionBelowPremium",
    label: meta.Label,
    description: meta.Description,
    count,
  };
};

/** Errored compliance cards + total issue count for app-bar drilldown. */
export const useComplianceIssueCount = (enabled: boolean) => {
  const { currentGroup } = useContext(AuthContext);
  const [issueCards, setIssueCards] = useState<ComplianceIssueCardSummary[]>(
    []
  );
  const [loading, setLoading] = useState(false);

  const reload = useCallback(() => {
    if (!enabled || !currentGroup) {
      setIssueCards([]);
      return Promise.resolve();
    }
    setLoading(true);
    return Promise.all([
      getDocumentComplianceApi(String(currentGroup)),
      getCompanyMembersApi(String(currentGroup)).catch(() => null),
    ])
      .then(([complianceRes, membersRes]) => {
        const apiCards: ComplianceCard[] = Array.isArray(
          complianceRes?.data?.Documents
        )
          ? complianceRes.data.Documents
          : [];
        const members = Array.isArray(membersRes?.data)
          ? membersRes.data
          : [];

        const byKey = new Map<string, ComplianceIssueCardSummary>();
        apiCards.forEach((card) => {
          if (card.Key === "PendingLapse") return;
          const summary = toSummary(card);
          if (summary) byKey.set(summary.key, summary);
        });

        if (!byKey.has("InvalidCellphone")) {
          const invalid = buildInvalidCellphoneSummary(members);
          if (invalid) byKey.set(invalid.key, invalid);
        }
        if (!byKey.has("DeductionBelowPremium")) {
          const deduction = buildDeductionBelowPremiumSummary(members);
          if (deduction) byKey.set(deduction.key, deduction);
        }

        const order = Object.keys(COMPLIANCE_CARD_META) as ComplianceCardKey[];
        const ordered = order
          .map((key) => byKey.get(key))
          .filter(Boolean) as ComplianceIssueCardSummary[];

        setIssueCards(ordered);
      })
      .catch(() => setIssueCards([]))
      .finally(() => setLoading(false));
  }, [enabled, currentGroup]);

  useEffect(() => {
    if (!enabled || !currentGroup) {
      setIssueCards([]);
      return;
    }
    reload();
    const interval = window.setInterval(() => reload(), POLL_MS);
    return () => window.clearInterval(interval);
  }, [enabled, currentGroup, reload]);

  const apiIssueCount = issueCards.reduce((sum, card) => sum + card.count, 0);

  return {
    issueCards,
    apiIssueCount,
    loading,
    reload,
  };
};
